<script>
  import { onDestroy, onMount } from "svelte";
  import { writable } from "svelte/store";
  import { firstLevelMenus } from "../../editor/menuSchema.js";
  import ItemResponse from "../component/ItemResponse.svelte";
  import { makeRGBAString, markMap } from "../../editor/component/mark.js";
  import {
    activeSpec,
    unlockedSpecs,
    currentSpec,
    menuConfig,
    callQueue,
  } from "../../editor/storage.js";
  import { datumMatch } from "../../editor/utility.js";
  import {
    clearHighlight,
    covertMarkConditionPredicateToObject,
    parseDatumObject,
  } from "../../editor/direct.js";

  // event functions
  export let inputFocusAction;
  export let inputBlurAction;

  let datumObject = writable(undefined);
  let layerIndex = writable(undefined);
  let layerName = writable(undefined);
  let currMark, layerMark;
  let currConditions = {};
  let impossible = false;
  const allowed_properties = [
    "fill",
    "stroke",
    "size",
    "strokeWidth",
    "opacity",
    "shape",
  ];
  function setValues() {
    if ($layerIndex !== undefined && $datumObject !== undefined) {
      let mark = $currentSpec.fronto?.layer?.[$layerIndex]?.mark || {};
      if (!currMark) currMark = mark.type;
      layerMark = mark.type;
      if (layerMark !== currMark) {
        if (layerMark === "line" && currMark === "point")
          impossible = "pointOnLine";
        else if (layerMark === "area" && currMark === "point")
          impossible = "pointOnArea";
        else impossible = true;
      }
      if (!impossible) {
        if ($currentSpec.fronto?.layer?.[$layerIndex])
          layerName.set($currentSpec.fronto?.layer?.[$layerIndex].name);
        allowed_properties.forEach((property) => {
          currConditions[property] = undefined;
          if (mark[property]?.conditions?.constructor.name === "Array") {
            mark[property].conditions.forEach((cond) => {
              if (
                cond.condition &&
                datumMatch(
                  covertMarkConditionPredicateToObject(cond.condition),
                  $datumObject
                )
              ) {
                currConditions[property] = cond.value;
              }
            });
          }
        });
      }
    }
  }
  function clearValues() {
    layerIndex.set(undefined);
    datumObject.set(undefined);
    currConditions = {};
    layerName.set(undefined);
    layerMark = undefined;
    impossible = false;
  }

  function updateMarkIndividual(event, role) {
    let rule;
    let is_empty_condition = Object.values(currConditions).every(
      (d) => d == undefined
    );
    if (role === "data-set" || (role === "data" && is_empty_condition)) {
      datumObject.set(event.datum);
    } else if (role === "data" && !is_empty_condition) {
      // update data
      rule = {
        category: "manual",
        description: "Update data point to conditionalize",
        specifier: { role: "mark", data: $datumObject },
        action: "modify",
        option: { data: event.datum },
      };
      if ($layerName !== undefined) {
        rule.specifier.id = $layerName;
      } else if ($layerIndex !== undefined) {
        rule.specifier.index = $layerIndex;
      }
    } else if (allowed_properties.includes(role)) {
      rule = {
        description: `Change ${role} of selected mark(s)`,
        specifier: { role: "mark", data: $datumObject },
        action: "modify",
        option: {},
      };
      if ($layerName !== undefined) {
        rule.specifier.id = $layerName;
      } else if ($layerIndex !== undefined) {
        rule.specifier.index = $layerIndex;
      }
      let newValue;
      if (["fill", "stroke"].includes(role)) {
        newValue =
          makeRGBAString(
            event.detail.r,
            event.detail.g,
            event.detail.b,
            event.detail.a
          ) || null;
      } else if (["size", "opacity", "strokeWidth"].includes(role)) {
        newValue = parseFloat(event.target.value) || null;
      } else {
        rule = undefined;
      }
      rule.option[role] = newValue;
    }
    if (rule) {
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    }
  }

  menuConfig.subscribe((config) => {
    if (config?.layerIndex !== undefined && config?.datum) {
      layerIndex.set(config.layerIndex);
      datumObject.set(config.datum);
      currMark = config.mark.split(" ")[0];
    } else {
      clearValues();
    }
    setValues();
  });

  datumObject.subscribe((obj) => {
    let aria_role = currMark;
    if (aria_role === "line") aria_role += " mark";
    // let aria_role2 = layerMark;
    // if (aria_role2 === "line") aria_role2 += " mark";
    clearHighlight();
    document
      .querySelectorAll(
        "#view-" + $currentSpec.uid + " " + `path[role=graphics-symbol]`
      )
      .forEach((path) => {
        if (path.getAttribute("aria-roledescription") === aria_role) {
          console.log(parseDatumObject(path.getAttribute("aria-label")), obj);
          if (
            datumMatch(
              parseDatumObject(path.getAttribute("aria-label")),
              obj,
              true
            )
          ) {
            console.log(path);
            path.classList.add("dupo-g-found-specific");
          }
        }
      });
  });

  // pipelines
  onMount(() => {
    setValues();
  });
  currentSpec.subscribe(() => {
    setValues();
  });
  onDestroy(() => {
    clearValues();
  });
</script>

<section class="second-level-menu">
  <h2>
    {firstLevelMenus["mark_individual"]?.title || "Mark"}
  </h2>

  {#if $activeSpec == -1}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Create or select an artboard!</p>
      </div>
    </div>
  {:else if !$unlockedSpecs.includes($currentSpec.uid)}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Unlock this artboard first!</p>
      </div>
    </div>
  {:else}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Edit property for the selected mark(s).</p>
      </div>
    </div>
    {#if !$datumObject}
      <div class="dupo-menu-pane mb-2">
        <ItemResponse
          type="data-object"
          title="Data mark to edit"
          token="datum"
          {inputFocusAction}
          {inputBlurAction}
          value={{}}
          editable={true}
          changeAction={(event) => {
            updateMarkIndividual(event, "data-set");
          }}
        />
      </div>
    {:else}
      <div class="dupo-menu-pane mb-2">
        <ItemResponse
          type="data-object"
          title="Data mark to edit"
          token="datum"
          {inputFocusAction}
          {inputBlurAction}
          value={{ role: currMark, datum: $datumObject }}
          editable={true}
          changeAction={(event) => {
            updateMarkIndividual(event, "data");
          }}
        />
      </div>
      {#if impossible}
        <div class="dupo-menu-pane mb-2">
          <div class="dupo-menu-pane-row">
            <p class="text-sm">
              {#if impossible === "pointOnLine"}Point marks on line marks can't
                be individually styled.
              {:else if impossible === "pointOnArea"}Point marks on area mark
                curves can't be individually styled.
              {:else}The current mark type ({currMark}) does not support
                individual styling.
              {/if}
            </p>
          </div>
        </div>
      {:else if currMark}
        <div class="pane-divider" />
        <!-- properties -->
        <!-- fill -->
        {#if markMap[currMark]?.encodings?.includes("fill")}
          <div class="dupo-menu-pane mb-2">
            <ItemResponse
              type="color"
              title="Fill color"
              token="fill"
              {inputFocusAction}
              {inputBlurAction}
              value={currConditions.fill || "rgba(0,0,0,1)"}
              changeAction={(event) => {
                updateMarkIndividual(event, "fill");
              }}
            />
          </div>
        {/if}
        <!-- stroke -->
        {#if markMap[currMark]?.encodings?.includes("stroke")}
          <div class="dupo-menu-pane mb-2">
            <ItemResponse
              type="color"
              title="Stroke color"
              token="stroke"
              {inputFocusAction}
              {inputBlurAction}
              value={currConditions.stroke || "rgba(0,0,0,1)"}
              changeAction={(event) => {
                updateMarkIndividual(event, "stroke");
              }}
            />
          </div>
        {/if}
        <!-- opacity -->
        {#if markMap[currMark]?.encodings?.includes("opacity")}
          <div class="dupo-menu-pane mb-2">
            <ItemResponse
              type="opacity"
              title="Opacity"
              token="opacity"
              {inputFocusAction}
              {inputBlurAction}
              value={currConditions.opacity || 1}
              changeAction={(event) => {
                updateMarkIndividual(event, "opacity");
              }}
            />
          </div>
        {/if}
        <!-- size -->
        {#if markMap[currMark]?.encodings?.includes("size")}
          <div class="dupo-menu-pane mb-2">
            <ItemResponse
              type="size"
              title="Size"
              token="size"
              {inputFocusAction}
              {inputBlurAction}
              value={currConditions.size}
              changeAction={(event) => {
                updateMarkIndividual(event, "size");
              }}
            />
          </div>
        {/if}
        <!-- strokeWidth -->
        {#if markMap[currMark]?.encodings?.includes("strokeWidth")}
          <div class="dupo-menu-pane mb-2">
            <ItemResponse
              type="size"
              title="Stroke width"
              token="strokeWidth"
              {inputFocusAction}
              {inputBlurAction}
              value={currConditions.strokeWidth}
              changeAction={(event) => {
                updateMarkIndividual(event, "strokeWidth");
              }}
            />
          </div>
        {/if}
        {#if markMap[currMark]?.encodings?.includes("shape")}
          <!-- shape -->
          <div class="dupo-menu-pane mb-2">
            <ItemResponse
              type="shape"
              title="Point shape"
              token="shape"
              {inputFocusAction}
              {inputBlurAction}
              value={currConditions.shape}
              changeAction={(event) => {
                updateMarkIndividual(event, "shape");
              }}
            />
          </div>
        {/if}
      {/if}
    {/if}
  {/if}
</section>
